import { Component, inject } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import {
  confirmPasswordValidator,
  emailPhoneValidator,
} from '../../shared/util/auth.validators';

import * as UserActions from '../../shared/core/state/users/user.actions';
import { UserService } from '../../shared/core/state/users/users.service';

@Component({
  selector: 'black-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.css',
  standalone: false,
})
export class PasswordResetComponent {
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  private userService = inject(UserService);

  userNameInputForm: FormGroup;
  changePasswordForm: FormGroup;

  username: string | undefined;
  resetToken: string | undefined;
  currentSection = 1;
  showPassword = false;
  showConfirmPassword = false;

  constructor() {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    this.store.dispatch(UserActions.initiateResetPasswordFlow());

    this.userNameInputForm = this.fb.group({
      username: [
        '',
        {
          validators: [Validators.required, emailPhoneValidator],
        },
      ],
    });

    this.changePasswordForm = this.fb.group(
      {
        password: [
          '',
          {
            validators: [
              Validators.required,
              Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}'),
            ],
          },
        ],
        confirm_password: ['', Validators.required],
      },
      { validators: confirmPasswordValidator }
    );
  }

  get usernameInput() {
    return this.userNameInputForm.get('username');
  }

  get passwordInput() {
    return this.changePasswordForm.get('password');
  }

  get confirmPasswordInput() {
    return this.changePasswordForm.get('confirm_password');
  }

  get isInputValid() {
    return this.userNameInputForm.valid;
  }

  intiateResetRequest() {
    this.usernameInput?.setValue(this.usernameInput.value.trim());
    this.userNameInputForm.markAllAsTouched();

    if (this.userNameInputForm.valid) {
      this.username = this.usernameInput?.value;
      this.currentSection = 2;
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }

  sendTokenRequest(otpCode: string) {
    if (this.username) {
      this.store.dispatch(
        UserActions.sendResetPasswordRequest({
          code: otpCode,
          username: this.username,
        })
      );

      this.store
        .select((state) => state.users.resetToken)
        .subscribe((token) => {
          if (token && token !== '') {
            this.currentSection = 3;
            this.resetToken = token;
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          }
        });
    }
  }

  sendCodeRequest() {
    if (!this.username) {
      console.warn('Username is undefined. Cannot send verification code.');
      return;
    }

    this.store.dispatch(
      UserActions.sendVerificationCode({ username: this.username })
    );
  }

  sendResetPasswordRequest() {
    if (this.changePasswordForm.valid && this.resetToken) {
      const { password } = this.changePasswordForm.value;

      this.store.dispatch(
        UserActions.resetPassword({
          token: this.resetToken,
          password,
        })
      );

      this.store
        .select((state) => state.users.resetPasswordSuccess)
        .subscribe((isComplete) => {
          if (isComplete) {
            this.currentSection = 4;
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          }
        });
    }
  }
}
